import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import StreamDestinationSettings from './templates/stream-destination-settings';
import EventSettings from './templates/event-settings';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import { GreenroomState } from '../../stores/types/types';
import AvatarPhoto from './templates/avatar-photo';
import { setModalOpen } from '../../stores/actions/modal';
import ShotBuilder from './templates/shot-builder/shot-builder';
import DeleteShotConfirmation from './templates/delete-shot-confirmation';
import CopyEvent from './templates/copy-event';
import DeleteEventConfirmation from './templates/delete-event-confirmation';
import ProgramFeed from './templates/program-feed';
import VideoEditor from './templates/video-editor/video-editor';
import OnsiteFeed from './templates/onsite-feed';
import DeleteAccount from './templates/delete-account';
import TutorialVideo from '../help/tutorial-video';
import MultiAccountLoginConfirmation from './templates/multi-account-login-confirmation';
import LiveStreamConfirmation from './templates/live-stream-confirmation';
import Socket from '../../connection/socket';
import DeleteChatMessageConfirmation from './templates/delete-chat-message-confirmation';
import NoRoomAccess from './templates/no-room-access';
import useCloseEditModal from '../../hooks/use-close-edit-modal';

interface ModalProps {
	socket?: Socket | null | undefined
}

export default function Modal(props: ModalProps) {
	const { socket } = props;

	const dispatch = useDispatch();
	const modalOpen = useSelector((state: GreenroomState) => state.ModalReducer.modalOpen);
	const modalTemplate = useSelector((state: GreenroomState) => state.ModalReducer.modalTemplate);
	const modalData = useSelector((state: GreenroomState) => state.ModalReducer.modalData);

	const modalBackground = useRef<HTMLDivElement>(null);

	const closeEditModal = useCloseEditModal();

	const MODALS_REQUIRING_CLICK_TO_CLOSE = [
		MODAL_TEMPLATES.PROGRAM_FEED,
		MODAL_TEMPLATES.MULTI_ACCOUNT_LOGGED_IN,
	];

	const closeModal = (event: any) => {
		if (
			event.target === modalBackground.current
			&& !MODALS_REQUIRING_CLICK_TO_CLOSE?.includes(modalTemplate)
		) {
			if (
				modalTemplate === MODAL_TEMPLATES.SHOT_BUILDER
				|| modalTemplate === MODAL_TEMPLATES.DELETE_SHOT_CONFIRMATION
			) {
				closeEditModal(socket, false);
			} else {
				dispatch(setModalOpen(false));
			}
		}
	};

	return (
		modalOpen ? (
			<div
				id='modal'
				className={`info-modal-background ${modalTemplate}`}
				ref={modalBackground}
				onClick={closeModal}
			>
				{modalTemplate === MODAL_TEMPLATES.EVENT_SETTINGS
					&& (
					<EventSettings
						showSession={modalData.showSession}
						showSessionTypes={modalData.showSessionTypes}
						socket={modalData.socket}
						modalInfoAction={modalData.modalInfoAction}
						showInvitations={modalData.showInvitations}
					></EventSettings>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.STREAM_DESTINATION_SETTINGS
					&& <StreamDestinationSettings
					destinationType={modalData.destinationType}
					></StreamDestinationSettings>}
				{modalTemplate === MODAL_TEMPLATES.AVATAR_PHOTO
					&& (
						<AvatarPhoto
							isOnboarding={modalData.isOnboarding}
							modalAvatarAction={modalData.modalAvatarAction}
						></AvatarPhoto>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.SHOT_BUILDER
					&& (
						<ShotBuilder
							socket={modalData.socket}
							modalPage={modalData.modalPage}
							modalTab={modalData.modalTab}
						/>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.DELETE_SHOT_CONFIRMATION
					&& <DeleteShotConfirmation socket={modalData.socket} />
				}
				{modalTemplate === MODAL_TEMPLATES.COPY_EVENT
					&& <CopyEvent sourceMeeting={modalData.sourceMeeting} />
				}
				{modalTemplate === MODAL_TEMPLATES.DELETE_EVENT_CONFIRMATION
					&& <DeleteEventConfirmation meeting={modalData.meeting} />
				}
				{modalTemplate === MODAL_TEMPLATES.PROGRAM_FEED
					&& <ProgramFeed />
				}
				{modalTemplate === MODAL_TEMPLATES.ONSITE_FEED
					&& <OnsiteFeed />
				}
				{modalTemplate === MODAL_TEMPLATES.VIDEO_EDITOR
					&& (
						<VideoEditor
							mediaId={modalData.mediaId}
							editorPage={modalData.editorPage}
						/>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.DELETE_ACCOUNT
					&& (
						<DeleteAccount
						/>
					)
				}
				{modalTemplate === MODAL_TEMPLATES.HELP_MENU_TUTORIAL_VIDEO && (
					<TutorialVideo
						title="Greenroom Getting Started Producer Video"
						video={
							// need to do this because of opening in greenroom
							window.location.pathname.includes('/producer')
								? 'https://fast.wistia.net/embed/iframe/pew8a4toq4?seo=false&videoFoam=true'
								: 'https://fast.wistia.net/embed/iframe/7vwxovajnn?seo=false&videoFoam=true'
						}
						hasHeader={true}
						type="normal"
					/>
				)}
				{modalTemplate === MODAL_TEMPLATES.PRESENTER_ONBOARDING_TUTORIAL_VIDEO && (
					<TutorialVideo
						title="Greenroom Getting Started Presenter Video"
						video="https://fast.wistia.net/embed/iframe/7vwxovajnn?seo=false&videoFoam=true"
						hasHeader={false}
						type="normal"
					/>
				)}
				{modalTemplate === MODAL_TEMPLATES.MULTI_ACCOUNT_LOGGED_IN && (
					<MultiAccountLoginConfirmation socket={socket} />
				)}
				{modalTemplate === MODAL_TEMPLATES.LIVE_STREAM_CONFIRMATION && (
					<LiveStreamConfirmation
						socket={modalData.socket}
						initialMode={modalData.initialMode}
					/>
				)}
				{modalTemplate === MODAL_TEMPLATES.DELETE_CHAT_MESSAGE_CONFIRMATION && (
					<DeleteChatMessageConfirmation
						chatPost={modalData.chatPost}
						socket={modalData.socket}
					/>
				)}
				{modalTemplate === MODAL_TEMPLATES.NO_ROOM_ACCESS && (
					<NoRoomAccess />
				)}
			</div>
		) : <></>
	);
}
